import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTypedSelector } from '../../../../../hooks/useTypeSelector';
import { axiosInstance } from '../../../../../config/axiosInstance';
import { globalEnv } from '../../../../../config/baseConfig';
import { useQuery } from 'react-query';
import { sortingOrder } from '../../../../Utils/sortingOrder';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import TableLoadingSkeleton from '../../../../Utils/LoadingAnimations/TableLoadingSkeleton';
import Pagination from '../../../../Tables/pagination';
import { DataNotFoundAnimation } from '../../../../Utils/ErrorAnimations/dataNotFoundAnim';
import moment from 'moment';
import { HeaderItem } from '../../../../Tables/TablesList/Components/common';

export function PendingEmailsList() {
	const { campaignId } = useParams();

	const [selectedHeader, setSelectedHeader] = useState('createdAt');

	const [page, setPage] = useState(1);
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const { data, isLoading, isFetching, isError } = useQuery(
		['getEmailsList', page],
		() => fetchEmailList(),
		{
			cacheTime: 0,
			keepPreviousData: false,
			staleTime: 5000,
			refetchOnWindowFocus: false
		}
	);

	const fetchEmailList = async () => {
		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};

		const url =
			campaignId != null
				? `/outreach/campaign/${campaignId}/pending-emails`
				: `/outreach/campaign/all-pending-emails`;

		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL + url + '?page=' + page,
			config
		);

		return data;
	};

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Domain"
					slugName="domain"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Receiver"
					slugName="receiver"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Object"
					slugName="object"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Creation date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
			</tr>
		);
	};

	return (
		<section className="pl-16 pt-16">
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-semibold uppercase dark:text-primary-text-dark text-primary-text-light ">
									<TableHeaders />
								</thead>
							)}
							<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
								{isLoading || isFetching || isError ? (
									<></>
								) : (
									data.data.map((item: any) => (
										<TableData
											key={item._id}
											id={item._id}
											domain={item.domain}
											receiver={item.receiver}
											object={item.object}
											createdAt={item.createdAt}
											// image={
											// 	item.coverPic != null
											// 		? item.coverPic
											// 		: item.backgroundImage
											// }
										/>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>
		</section>
	);
}

type tableDateType = {
	id: string;
	domain: string;
	receiver: string;
	object: string;
	createdAt: string;
};

const TableData = ({
	id,
	domain,
	receiver,
	object,
	createdAt
}: tableDateType) => {
	return (
		<tr>
			<td className="col-span-1 p-2 break-all">
				<div className="text-center dark:text-gray-400">{domain}</div>
			</td>

			<td className="col-span-1 p-2 break-all">
				<div className="text-center dark:text-gray-400">{receiver}</div>
			</td>

			<td className="col-span-1 p-2 break-all">
				<div className="text-center dark:text-gray-400">{object}</div>
			</td>

			<td className="p-2 whitespace-nowrap w-20">
				<div className="text-center font-medium dark:text-gray-400">
					{moment(createdAt).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
		</tr>
	);
};
